import {CHAT_KEYS} from "@/api/chatApi";
import Loader from "@/components/Common/kpi/Loader";
import NotificationBadge from "@/components/NavBar/NotificationBadge";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import {useOpenChat} from "@/hooks/useOpenChat";
import {useGenericInfiniteQuery} from "@/hooks/useQueryHooks";
import {useTimeFormat} from "@/hooks/useTimeFormat";
import ProfileIcon from "@/images/navbar_logged_out_profile_icon.svg?react";
import clsxm from "@/lib/clsxm";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import type {TChat} from "@/store/types";
import dayjs from "dayjs";
import {useAtomValue} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const ChatTab: FC = () => {
  const {t} = useTranslation(["navigation", "common"]);
  const formatTime = useTimeFormat();
  const companyId = useAtomValue(selectedCompanyIdAtom);
  const chatListQuery = useGenericInfiniteQuery<TChat>({
    queryKey: CHAT_KEYS.qChatList({companyId: companyId!}),
    enabled: !!companyId,
  });
  const {ref} = useInViewWithQuery(chatListQuery);
  const openChat = useOpenChat();

  return (
    <div
      className={clsxm(
        "divide-neutral-white-dark w-full divide-y",
        chatListQuery.isLoading ? "flex flex-col" : "h-[60vh]",
      )}>
      {chatListQuery.isLoading && (
        <Loader
          styles={{
            loaderClassName: "size-10",
            containerClassName: "items-center size-20",
          }}
        />
      )}
      <div className="w-full py-5">
        <span className="text-neutral-black border-neutral-white-dark flex border-b px-5 text-xl font-semibold">
          {t("messages")}
        </span>
      </div>
      {chatListQuery.data?.map(item => {
        const hasUnseen = !!item.unread_by_company;
        return (
          <button
            key={item.id}
            className={clsxm(
              "flex h-28 w-full items-center justify-between gap-10 px-5 py-4",
              hasUnseen ? "bg-soft-grey-100" : "bg-white",
              "hover:bg-neutral-white-dark",
            )}
            onClick={() => {
              openChat({
                influencerUserId: item.user.id,
                campaignId: item.campaign.id,
                companyId: +companyId!,
                unreadCount: item.unread_by_company ?? 0,
                messageId: item.id,
              });
            }}>
            <div className="flex w-full flex-col items-start justify-between gap-2">
              <div className="flex items-center gap-2.5 pt-2.5">
                {item.user.influencer_profile.picture320 ? (
                  <img
                    className="border-neutral-white-dark h-7 w-7 rounded-full border object-fill"
                    src={item.user.influencer_profile.picture320}
                    alt="Influencer"
                    style={{borderStyle: "solid"}}
                  />
                ) : (
                  <div className="border-neutral-white-dark flex h-7 w-7 items-center justify-center rounded-full border bg-white p-1.5">
                    <ProfileIcon />
                  </div>
                )}
                <span className="text-neutral-black text-base font-medium">{item.user.first_name}</span>
              </div>

              <span className="text-neutral-grey-dark line-clamp-2 w-full text-left text-sm">
                {item.last_message?.text ?? ""}
              </span>

              {!!item.last_message?.created && (
                <span className="text-neutral-grey-dark pb-1.5 text-[10px]">
                  {formatTime(dayjs(item.last_message.created))}
                </span>
              )}
            </div>

            {hasUnseen && <NotificationBadge isFilled count={item.unread_by_company ?? 0} />}
          </button>
        );
      })}
      {chatListQuery.data?.length === 0 && !chatListQuery.isLoading && (
        <div className="bg-primary-light-solid mx-5 my-7 rounded-md p-6">
          <span className="bg-linear-(--brand-medium-gradient) bg-clip-text text-center text-xl font-bold text-transparent">
            {t("common:chatTabEmptyState")}
          </span>
        </div>
      )}
      <div ref={ref} />
      {chatListQuery.isFetchingNextPage && <Loader styles={{containerClassName: "py-5 shrink-0"}} />}
    </div>
  );
};

export default ChatTab;
